import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Fuse from 'fuse.js';
import { SearchableOptionsModal } from '@repo/components/modals';
import { SearchableSection } from '@views/CommandBar/locallySearchableSections';
import { Key } from '@repo/components/tooltips';
import { setSearchTerm } from '@repo/redux/actions/search';
import { useSearchTips } from '@repo/hooks';
import styles from './SearchEducation.module.scss';

interface SearchTip {
  term: string;
  description: string;
  synonyms?: string[];
}

interface SearchTipMenuSection {
  title: string;
  suggestions: SearchTip[];
}

const createTipsSearch = (tips: SearchTip[]) => {
  const searchableTips = new Fuse(tips, {
    keys: ['term', 'description', 'synonyms'],
    threshold: 0.3,
  });

  return (term: string) => searchableTips.search(term).map(({ item }) => item) as SearchTip[];
};

const tipsListToSearchableSection = ([title, tips]: [string, SearchTip[]]) => {
  if (tips.length == 0) {
    return null;
  }

  return { title, unfilteredOptions: tips, search: createTipsSearch(tips) };
};

function filterMenuSection(
  { title, unfilteredOptions, search }: SearchableSection<SearchTip>,
  term: string,
): SearchTipMenuSection {
  let filteredOptions = unfilteredOptions;

  if (term) {
    filteredOptions = search(term);
  }

  if (!filteredOptions || (filteredOptions && filteredOptions.length == 0)) {
    return null;
  }

  return {
    title,
    suggestions: filteredOptions,
  };
}

const sectionOrder = ['basics', 'cleanup', 'up-to-date', 'within-field', 'categories', 'dates'];

const searchCategoryToSectionTitle = {
  'basics': 'Search Basics',
  'categories': 'Finding People by Category',
  'cleanup': 'Cleaning up and Managing Contacts',
  'dates': 'Searching by Date',
  'up-to-date': 'Keeping up to date',
  'within-field': 'Limiting Search to Specific Fields',
};

export default function SearchEducationModal({ closeModal }) {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState<string>('');
  const { categorizedTips } = useSearchTips();
  const [filteredSections, setFilteredSections] = useState([]);

  const searchableTips = useMemo(() => {
    return sectionOrder
      .map((category) => {
        return [searchCategoryToSectionTitle[category], categorizedTips[category] || []];
      })
      .map(tipsListToSearchableSection);
  }, [categorizedTips]);

  const filterSections = useCallback(
    (term: string) => {
      return searchableTips
        .map((section) => filterMenuSection(section, term))
        .filter((section) => section);
    },
    [searchableTips],
  );

  const handleSearch = useCallback(
    (value) => {
      setInputValue(value);
      setFilteredSections(filterSections(value));
    },
    [searchableTips],
  );

  const renderOption = useCallback(({ term, description }: SearchTip) => {
    return (
      <>
        {description}
        <Key className={styles.optionTerm}>{term}</Key>
      </>
    );
  }, []);

  const onOptionSelected = useCallback(({ term }: SearchTip) => {
    closeModal();
    dispatch(setSearchTerm(term, true));
  }, []);

  return (
    <SearchableOptionsModal
      searchTerm={inputValue}
      closeModal={closeModal}
      setSearchTerm={setInputValue}
      optionSections={filteredSections}
      handleSearch={handleSearch}
      renderOption={renderOption}
      onOptionSelected={onOptionSelected}
      placeholder="Explore search terms and operators"
      optionClassName={styles.option}
    />
  );
}
